/**
 * Phase 3G: Export Gate Engine
 * 
 * Hard gate checklist that MUST pass before a case can reach EXPORT_READY.
 * Production: blocks export if any gate fails.
 * Demo: runs validation but allows export preview.
 * 
 * CLOSED-LOOP ENFORCEMENT:
 * 1. All exported CPTs are authorized (no exceptions)
 * 2. All exported CPTs have documentation_status=SUFFICIENT
 * 3. All exported CPTs have surgeon_confirmation=CONFIRMED_PERFORMED
 * 4. Modifier logic validated (no conflicts)
 * 5. Assistant surgeon justification complete when assistant code present
 * 6. Audit timeline exists for critical events
 */

import { checkClosedLoopCompliance } from '@/lib/phase3/boundaries';

// ============================================================================
// TYPES
// ============================================================================

export type GateStatus = 'passed' | 'failed' | 'warning';

export interface ExportGateCheck {
  id: string;
  label: string;
  description: string;
  status: GateStatus;
  severity: 'blocking' | 'warning';
  /** If failed, what needs to be fixed */
  failureDetail?: string;
  /** Quick-link action to resolve the issue */
  quickAction?: ExportGateQuickAction;
  /** Related CPT code, if applicable */
  cptCode?: string;
  /** Closed-loop stage this gate validates */
  closedLoopStage: 'authorized' | 'performed' | 'documented' | 'exportable' | 'audit';
}

export type ExportGateQuickAction = 
  | { type: 'open_clarifications'; cptCode: string }
  | { type: 'open_confirmation'; cptCode: string }
  | { type: 'resolve_modifier_conflict'; cptCode: string }
  | { type: 'open_attestation' }
  | { type: 'view_audit_log' };

export interface ExportGateResult {
  canExport: boolean;
  gates: ExportGateCheck[];
  passedCount: number;
  failedCount: number;
  warningCount: number;
  totalCount: number;
  blockingFailures: ExportGateCheck[];
  warnings: ExportGateCheck[];
  /** Summary for display */
  summaryMessage: string;
}

// ============================================================================
// INPUT TYPES
// ============================================================================

export interface ExportGateCptInput {
  cptCode: string;
  cptDescription?: string;
  isAuthorized: boolean;
  documentationStatus: 'sufficient' | 'insufficient' | 'ambiguous' | 'not_found';
  surgeonConfirmation: 'confirmed_performed' | 'not_performed' | 'not_applicable' | 'deferred' | null;
  hasModifierConflicts: boolean;
  modifierConflictDetails?: string[];
  hasAssistantModifier: boolean;
  assistantJustificationComplete: boolean;
  hasPendingClarifications: boolean;
}

export interface ExportGateAuditInput {
  hasAuthorizationIntent: boolean;
  hasSubsetSelection: boolean;
  hasNoteUpload: boolean;
  hasAnalysis: boolean;
  hasPromptGeneration: boolean;
  hasConfirmations: boolean;
  hasExportEvent: boolean;
}

export interface ExportGateInput {
  cpts: ExportGateCptInput[];
  audit: ExportGateAuditInput;
  hasSurgeonAttestation: boolean;
  attestationTimestamp?: string;
  isDemoMode: boolean;
}

// ============================================================================
// GATE ENGINE
// ============================================================================

/**
 * Run all hard gate checks for export readiness.
 * 
 * In PRODUCTION: Export is BLOCKED if any blocking gate fails.
 * In DEMO: Validation runs, gates show status, but export is allowed.
 */
export function runExportGates(input: ExportGateInput): ExportGateResult {
  const gates: ExportGateCheck[] = [];

  // ── GATE 1: Surgeon Attestation ──────────────────────────────────
  gates.push({
    id: 'surgeon_attestation',
    label: 'Surgeon Attestation',
    description: 'Surgeon attestation for the authorization bundle exists',
    status: input.hasSurgeonAttestation ? 'passed' : 'failed',
    severity: 'blocking',
    failureDetail: !input.hasSurgeonAttestation 
      ? 'Missing surgeon attestation for the authorization bundle' 
      : undefined,
    quickAction: !input.hasSurgeonAttestation 
      ? { type: 'open_attestation' } 
      : undefined,
    closedLoopStage: 'authorized',
  });

  // ── GATE 2: At least one billable CPT ────────────────────────────
  const billableCpts = input.cpts.filter(
    c => c.isAuthorized && 
         c.documentationStatus === 'sufficient' && 
         c.surgeonConfirmation === 'confirmed_performed'
  );
  gates.push({
    id: 'has_billable_cpts',
    label: 'Billable CPTs Present',
    description: 'At least one CPT passes all closed-loop stages',
    status: billableCpts.length > 0 ? 'passed' : 'failed',
    severity: 'blocking',
    failureDetail: billableCpts.length === 0 
      ? 'No CPTs have passed all validation stages (authorized + documented + confirmed)' 
      : undefined,
    closedLoopStage: 'exportable',
  });

  // ── PER-CPT GATES ────────────────────────────────────────────────
  for (const cpt of input.cpts) {
    // Gate: Authorized
    gates.push({
      id: `authorized_${cpt.cptCode}`,
      label: `CPT ${cpt.cptCode} — Authorized`,
      description: 'CPT is in the surgeon-attested authorization bundle',
      status: cpt.isAuthorized ? 'passed' : 'failed',
      severity: 'blocking',
      failureDetail: !cpt.isAuthorized 
        ? `CPT ${cpt.cptCode} is not in the authorized bundle — cannot export unauthorized procedures` 
        : undefined,
      cptCode: cpt.cptCode,
      closedLoopStage: 'authorized',
    });

    // Gate: Documentation Sufficient
    gates.push({
      id: `documented_${cpt.cptCode}`,
      label: `CPT ${cpt.cptCode} — Documentation`,
      description: 'Required documentation elements are verified',
      status: cpt.documentationStatus === 'sufficient' ? 'passed' : 'failed',
      severity: 'blocking',
      failureDetail: cpt.documentationStatus !== 'sufficient'
        ? `CPT ${cpt.cptCode} documentation is ${cpt.documentationStatus}${cpt.hasPendingClarifications ? ' — clarifications pending' : ''}`
        : undefined,
      quickAction: cpt.documentationStatus !== 'sufficient' && cpt.hasPendingClarifications
        ? { type: 'open_clarifications', cptCode: cpt.cptCode }
        : undefined,
      cptCode: cpt.cptCode,
      closedLoopStage: 'documented',
    });

    // Gate: Surgeon Confirmation
    gates.push({
      id: `confirmed_${cpt.cptCode}`,
      label: `CPT ${cpt.cptCode} — Surgeon Confirmed`,
      description: 'Surgeon explicitly confirmed procedure was performed',
      status: cpt.surgeonConfirmation === 'confirmed_performed' ? 'passed' : 'failed',
      severity: 'blocking',
      failureDetail: cpt.surgeonConfirmation !== 'confirmed_performed'
        ? `CPT ${cpt.cptCode} — surgeon confirmation: ${cpt.surgeonConfirmation || 'pending'}`
        : undefined,
      quickAction: cpt.surgeonConfirmation !== 'confirmed_performed'
        ? { type: 'open_confirmation', cptCode: cpt.cptCode }
        : undefined,
      cptCode: cpt.cptCode,
      closedLoopStage: 'performed',
    });

    // Gate: Modifier Validation
    if (cpt.hasModifierConflicts) {
      gates.push({
        id: `modifier_${cpt.cptCode}`,
        label: `CPT ${cpt.cptCode} — Modifier Conflicts`,
        description: 'Modifier logic must be free of conflicts',
        status: 'failed',
        severity: 'blocking',
        failureDetail: `CPT ${cpt.cptCode} has modifier conflicts: ${cpt.modifierConflictDetails?.join('; ') || 'unknown'}`,
        quickAction: { type: 'resolve_modifier_conflict', cptCode: cpt.cptCode },
        cptCode: cpt.cptCode,
        closedLoopStage: 'exportable',
      });
    }

    // Gate: Assistant Surgeon Justification
    if (cpt.hasAssistantModifier && !cpt.assistantJustificationComplete) {
      gates.push({
        id: `assistant_${cpt.cptCode}`,
        label: `CPT ${cpt.cptCode} — Assistant Surgeon`,
        description: 'Assistant surgeon modifier requires documented justification',
        status: 'failed',
        severity: 'blocking',
        failureDetail: `CPT ${cpt.cptCode} has assistant surgeon modifier but justification is incomplete`,
        cptCode: cpt.cptCode,
        closedLoopStage: 'exportable',
      });
    }
  }

  // ── AUDIT TIMELINE GATES ─────────────────────────────────────────
  const auditChecks: Array<{ key: keyof ExportGateAuditInput; label: string }> = [
    { key: 'hasAuthorizationIntent', label: 'Authorization Intent' },
    { key: 'hasSubsetSelection', label: 'Subset Selection' },
    { key: 'hasNoteUpload', label: 'Op Note Upload' },
    { key: 'hasAnalysis', label: 'Evidence Analysis' },
    { key: 'hasConfirmations', label: 'Surgeon Confirmations' },
  ];

  for (const check of auditChecks) {
    const passed = input.audit[check.key];
    gates.push({
      id: `audit_${check.key}`,
      label: `Audit — ${check.label}`,
      description: `Audit trail entry exists for ${check.label.toLowerCase()}`,
      status: passed ? 'passed' : 'warning',
      severity: 'warning',
      failureDetail: !passed ? `Missing audit trail entry for: ${check.label}` : undefined,
      quickAction: !passed ? { type: 'view_audit_log' } : undefined,
      closedLoopStage: 'audit',
    });
  }

  // ── COMPUTE SUMMARY ──────────────────────────────────────────────
  const blockingFailures = gates.filter(g => g.status === 'failed' && g.severity === 'blocking');
  const warnings = gates.filter(g => g.status !== 'passed' && g.severity === 'warning');
  const passedCount = gates.filter(g => g.status === 'passed').length;

  // In demo mode, export is always allowed for preview
  const canExport = input.isDemoMode ? true : blockingFailures.length === 0;

  let summaryMessage: string;
  if (blockingFailures.length === 0 && warnings.length === 0) {
    summaryMessage = `All ${gates.length} gates passed. Export ready.`;
  } else if (blockingFailures.length === 0) {
    summaryMessage = `${passedCount}/${gates.length} gates passed with ${warnings.length} warning(s). Export allowed.`;
  } else {
    summaryMessage = `${blockingFailures.length} blocking issue(s) must be resolved before export.`;
  }

  return {
    canExport,
    gates,
    passedCount,
    failedCount: blockingFailures.length,
    warningCount: warnings.length,
    totalCount: gates.length,
    blockingFailures,
    warnings,
    summaryMessage,
  };
}

/**
 * Build gate input from reconciliation/sequencing data.
 * Helper to map from existing hooks into ExportGateInput.
 */
export function buildGateInputFromWorkflow(params: {
  cptStates: Record<string, {
    surgeonConfirmation: string | null;
    billableStatus: string;
    documentationStatus?: string;
  }>;
  authorizedCpts: string[];
  sequencedLines?: Array<{
    cptCode: string;
    reviewRequired: boolean;
    reviewReasons: string[];
    modifiers: string[];
  }>;
  hasSurgeonAttestation: boolean;
  attestationTimestamp?: string;
  hasOpenPromptsForCpt: (cptCode: string) => boolean;
  analysisComplete: boolean;
  isDemoMode: boolean;
}): ExportGateInput {
  const cpts: ExportGateCptInput[] = params.authorizedCpts.map(cptCode => {
    const state = params.cptStates[cptCode];
    const seqLine = params.sequencedLines?.find(l => l.cptCode === cptCode);
    const hasAssistant = seqLine?.modifiers.some(m => m === '80' || m === 'AS') || false;

    return {
      cptCode,
      isAuthorized: true, // All in authorizedCpts are authorized
      documentationStatus: (state?.documentationStatus || 'not_found') as ExportGateCptInput['documentationStatus'],
      surgeonConfirmation: (state?.surgeonConfirmation || null) as ExportGateCptInput['surgeonConfirmation'],
      hasModifierConflicts: seqLine?.reviewRequired || false,
      modifierConflictDetails: seqLine?.reviewReasons,
      hasAssistantModifier: hasAssistant,
      assistantJustificationComplete: hasAssistant, // Assume complete if applied by engine
      hasPendingClarifications: params.hasOpenPromptsForCpt(cptCode),
    };
  });

  return {
    cpts,
    audit: {
      hasAuthorizationIntent: params.hasSurgeonAttestation,
      hasSubsetSelection: params.hasSurgeonAttestation, // Subset is part of attestation flow
      hasNoteUpload: params.analysisComplete,
      hasAnalysis: params.analysisComplete,
      hasPromptGeneration: params.analysisComplete,
      hasConfirmations: cpts.some(c => c.surgeonConfirmation === 'confirmed_performed'),
      hasExportEvent: false, // Will be true after export
    },
    hasSurgeonAttestation: params.hasSurgeonAttestation,
    attestationTimestamp: params.attestationTimestamp,
    isDemoMode: params.isDemoMode,
  };
}
